/**
 * The `Withheld` catalog component: one row, an amber rail, and — since
 * 2026-09-11 — the figures whose SHAPE was refused.
 *
 * The design's rule (13j): a block that needs the licensed surface says so
 * in its own row, never by blurring the answer around it. The server alone
 * emits it, from a refusal — `withheld_block` in streaming/emitter.py — so
 * nothing here decides anything. Both props are bound by path like every
 * prop in this catalog, and the schema refuses a literal the same way
 * `Line`'s does: a sentence typed into the component rather than looked up
 * from the data model fails validation before any state changes.
 *
 * WHY FIGURES ARE SAFE ON A REFUSAL CARD, since that reads wrong at first.
 * What S2 withholds is the CLAIM a shape would make — a ranked screen is a
 * ranking whoever filled the cells — and never the values, which reached the
 * reader in licensed prose on the same turn. The server fills `facts` only
 * where the tool asserts nothing and the envelope holds exactly one row, so
 * there is no selection to read as a shortlist and no order to read as a
 * rank. A card with an empty list is the old card, unchanged.
 *
 * REPORTED AS AN EMPTY BOX. Drawn between two paragraphs with one sentence
 * in it, the card read as a gap in the answer rather than a statement about
 * one — and its sentence said "The facts are above" while the answer carried
 * on below it. `FIGURED_REASONS` supplies the other wording; this file only
 * has to draw what arrives.
 *
 * Not `FactSheet`, though it is exported and would have saved code: its
 * tiles are `bg-ground` and so is this card, so they would have been
 * invisible on it. A refusal is a note rather than a chart, so the figures
 * are set as a hairline-divided list — label left, figure right, the
 * grammar this product already uses wherever a label meets a value.
 *
 * Amber, because green and red belong to the market and amber to Mitra.
 * `--color-caution` is the widget theme's own, measured against the ground
 * it sits on in app/embed.css, and it is the colour the caveat under a tax
 * figure already uses for the same reason: a condition on an answer that
 * was given, not the red of one that was stopped.
 */
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'

import { cellText, humanLabel } from '@/features/mitra/lib/figures'
import { useLabelLang } from '@/features/mitra/panel/blocks/charts/labelLang'
import { FIGURE, SCROLL_X } from '@/features/mitra/panel/blocks/Card'
import type { FactPart } from '@/features/mitra/panel/blocks/charts/Facts'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'

export const WithheldApi = {
  name: 'Withheld',
  // OPTIONAL, and that is the contract: a refusal always has a sentence and
  // only sometimes has figures, so a card that required them would have to
  // refuse the case it exists for.
  schema: z.object({ reason: PathBinding, facts: PathBinding.optional() }).strict(),
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const withheldApi = WithheldApi as unknown as ComponentApi

export const Withheld = createComponentImplementation<ComponentApi>(
  withheldApi,
  ({ props }: { props: { reason?: unknown; facts?: unknown } }) => {
    const facts = listOf<FactPart>(props.facts)
    const lang = useLabelLang()
    return (
      <div
        data-role="withheld"
        data-component="withheld"
        role="note"
        className="mx-3 my-3 rounded-block border-l-4 border-caution bg-ground px-3 py-3
                 text-body-s leading-snug text-ink"
      >
        <div className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="mt-0 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-caution-wash text-caution"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M12 10v6M12 7h.01" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </span>
          <div className="min-w-0">
            {typeof props.reason === 'string' && <p className="break-words">{props.reason}</p>}
          </div>
        </div>
        {facts.length > 0 && (
          <dl className="mt-3 flex flex-col divide-y divide-rule border-t border-rule">
            {facts.map((f) => (
              <div
                key={f.id}
                data-role="withheld-fact"
                className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2"
              >
                <dt className="min-w-0 break-words">{humanLabel(f.label, lang)}</dt>
                {/* `FIGURE` is the one place tabular numerals are decided, so a
                  column of these lines up the way every other figure in the
                  product does. */}
                <dd className={`m-0 max-w-full text-ink ${FIGURE} ${SCROLL_X}`}>
                  {cellText(f.value)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    )
  },
)
